import { useEffect, useMemo, useState } from 'react';
import {
  api,
  type GiantKill,
  type League,
  type Player,
  type RatingMove,
  type Team,
  type WinRate,
  type WinRateBoard,
} from '../lib/api';
import { TRUERANK, TRUERANK_BY } from '../lib/rating/mark';
import { ratingText } from '../lib/rating/format';
import NumHead from './NumHead';
import { firstResultsOn, playedSeason } from '../lib/season-preview';
import { roundLabel } from '../lib/round-name';

/**
 * The season beyond the table: every player in one list, the upsets, and who is
 * moving.
 *
 * Below the standings rather than behind a sixth tab, which is both where
 * Season 1's site put them and the only place they fit — the tab bar is already
 * five wide on a 320px phone. A player scrolling past the table finds
 * themselves in a league-wide list, which is the question the table cannot
 * answer: the table says how my SIDE is doing, this says where I am.
 */

/** One row of the league-wide list: a player, and the squad they play for. */
interface Ranked {
  player: Player;
  team: string;
  teamId: number | null;
}

type SortKey = 'points' | 'rating' | 'won' | 'played' | 'sets' | 'games' | 'dd' | 'tiebreaks' | 'name';

const SORTS: { key: SortKey; label: string }[] = [
  { key: 'points', label: 'Total points' },
  { key: 'rating', label: TRUERANK },
  { key: 'won', label: 'Matches won' },
  { key: 'played', label: 'Matches played' },
  { key: 'sets', label: 'Sets won' },
  { key: 'games', label: 'Games won' },
  { key: 'dd', label: 'Dynamic Duo' },
  { key: 'tiebreaks', label: 'Tie-breaks won' },
  { key: 'name', label: 'Name' },
];

/**
 * What each sort reads, as a number. Higher is better for every one of them,
 * so the table sorts descending throughout and never has to explain itself.
 *
 * A player with no figure at all sorts LAST rather than as a nought — an
 * unrated substitute is not the worst-rated player in the league, and putting
 * them at the bottom of a rating sort says exactly that. Negative infinity is
 * the only value that means "below everything" without being a score.
 */
function value(row: Ranked, key: SortKey): number {
  const s = row.player.stats;
  switch (key) {
    case 'points':
      return s?.totalPoints ?? Number.NEGATIVE_INFINITY;
    case 'rating':
      return row.player.trueRank?.rating ?? row.player.rating ?? Number.NEGATIVE_INFINITY;
    case 'won':
      return s?.matchesWon ?? Number.NEGATIVE_INFINITY;
    case 'played':
      return s?.played ?? Number.NEGATIVE_INFINITY;
    case 'sets':
      return s?.setsWon ?? Number.NEGATIVE_INFINITY;
    case 'games':
      return s?.gamesWon ?? Number.NEGATIVE_INFINITY;
    case 'dd':
      return s?.ddPoints ?? Number.NEGATIVE_INFINITY;
    case 'tiebreaks':
      return row.player.tieBreaks?.won ?? Number.NEGATIVE_INFINITY;
    case 'name':
      return 0;
  }
}

/**
 * What settles a tie on the chosen column, in order, before the name does.
 *
 * Points, then sets, then games: the order the league ranks by everywhere
 * else. It was the name straight away, and a player asked why Isa Nana sat
 * 22nd behind Imtiaz Mahomed on "Matches won" with 36 games to Imtiaz's 35 —
 * the answer was "because Im comes before Is", which is no answer at all.
 * The name stays as the last word, so two players level on everything still
 * come out in the same order every load.
 */
const TIE_BREAKS: SortKey[] = ['points', 'sets', 'games'];

export function rank(rows: Ranked[], key: SortKey): Ranked[] {
  const sorted = [...rows];
  const keys = key === 'name' ? [] : [key, ...TIE_BREAKS.filter((k) => k !== key)];
  sorted.sort((a, b) => {
    for (const k of keys) {
      const left = value(a, k);
      const right = value(b, k);
      /*
       * Compared, not subtracted. Two unrated players are both -Infinity and
       * subtracting gives NaN, which sort treats as "leave them alone" — so the
       * unrated tail would come out in load order rather than alphabetically,
       * which is the reshuffling the name at the end exists to prevent. `!==`
       * says they are level and lets them fall through to the next key.
       */
      if (left !== right) return right - left;
    }
    // Level on everything: the name, so the list does not reshuffle between loads.
    return a.player.name.localeCompare(b.player.name);
  });
  return sorted;
}

const n = (v: number | null | undefined, dash = '–') =>
  v === null || v === undefined ? dash : `${v}`;

/**
 * A player and their club in one cell.
 *
 * A column of its own is what a wide screen would give this, and these
 * boards are read on a phone. "Bed Shop Dream Team" wrapped to three lines
 * inside a 90px column, which made every row of that club three rows tall
 * and took the width from the figures beside it. Stacked under the name it
 * wraps at most once, the column goes, and nothing is hidden.
 */
function NameAndTeam({ name, team }: { name: string; team: string | null }) {
  return (
    <td className="st-who">
      <span>{name}</span>
      {team && <small className="muted">{team}</small>}
    </td>
  );
}

export default function SeasonStats({
  seasonId,
  league,
}: {
  seasonId?: number;
  league?: League | null;
}) {
  const [teams, setTeams] = useState<Team[] | null>(null);
  const [subs, setSubs] = useState<Player[]>([]);
  const [kills, setKills] = useState<GiantKill[]>([]);
  const [upsets, setUpsets] = useState<{ playerId: number; name: string; team: string; upsets: number }[]>([]);
  const [movers, setMovers] = useState<
    { gameweek: number; stage?: string; risers: RatingMove[]; fallers: RatingMove[] } | null
  >(null);
  const [winRates, setWinRates] = useState<WinRateBoard | null>(null);
  /*
   * Whether the upset board's figures are live TrueRanks or the season's
   * seeds — highlights.ts decides per season and says so. Tracked because the
   * TrueRank colour has to mean TrueRank: painting a seed in it on a season
   * measured off seeds would make the colour a decoration instead of a fact.
   */
  const [ratingsFrom, setRatingsFrom] = useState<'truerank' | 'seed'>('seed');
  const [team, setTeam] = useState<string>('');
  const [sort, setSort] = useState<SortKey>('points');

  useEffect(() => {
    if (!seasonId) return;
    setTeams(null);
    api
      .teams(seasonId)
      .then((data) => {
        setTeams(data.teams);
        setSubs(data.substitutes ?? []);
      })
      // A stats section is never worth surfacing an error for; the table above
      // it is the page's job and it has already loaded.
      .catch(() => setTeams([]));
    api
      .highlights(seasonId)
      .then((data) => {
        setKills(data.giantKillers);
        setUpsets(data.upsetLeaderboard);
        setMovers(data.movers);
        setWinRates(data.winRates ?? null);
        setRatingsFrom(data.ratingsFrom);
      })
      .catch(() => {
        setKills([]);
        setUpsets([]);
        setMovers(null);
        setRatingsFrom('seed');
      });
  }, [seasonId]);

  const rows: Ranked[] = useMemo(() => {
    const all: Ranked[] = (teams ?? []).flatMap((t) =>
      t.players.map((player) => ({ player, team: t.name, teamId: t.id })),
    );
    // Substitutes belong to no squad but do play, and leaving them out would
    // make the league-wide list quietly not league-wide.
    for (const player of subs) all.push({ player, team: 'Substitute', teamId: null });
    return all;
  }, [teams, subs]);

  const shown = useMemo(
    () => rank(team ? rows.filter((r) => r.team === team) : rows, sort),
    [rows, team, sort],
  );

  /*
   * Columns appear only where the season has something to put in them, the same
   * rule the squad tables use. A column of dashes is a promise the season
   * cannot keep — Season 1 recorded no tie-breaks at all, and a TB header over
   * eleven rounds of nothing reads as a fault rather than as an absence.
   */
  const tiered = league?.playerGrading === 'tier';
  const rated = rows.some((r) => r.player.trueRank);
  // Appended to the upset board's rating spans, and empty on a season whose
  // figures are seeds. See ratingsFrom above.
  const tv = ratingsFrom === 'truerank' ? ' tr-value' : '';
  const anyDd = rows.some((r) => (r.player.stats?.ddPoints ?? 0) !== 0);
  const anyTb = rows.some((r) => r.player.tieBreaks?.played);
  const played = rows.some((r) => (r.player.stats?.played ?? 0) > 0);

  if (!seasonId) return null;
  if (teams === null) return null;

  /*
   * Before a ball is hit there is nothing to rank, so the section says what is
   * COMING rather than showing a leaderboard of noughts.
   *
   * Worth more than it looks. A player who does not know their rating will move
   * has no reason to open this on a Monday, and the season's own build-up is
   * the only time anyone reads a page like this closely.
   *
   * And it points at the season before, which is the strongest thing available:
   * every board below, already full, with the same people's names in it. "Here
   * is what this looks like" beats "here is what this will do", and it costs
   * nothing because the data has been there all along.
   */
  if (!played) {
    const example = playedSeason(league?.seasons, seasonId);
    const opens = firstResultsOn(league?.seasons.find((s) => s.id === seasonId)?.startsOn);
    return (
      <section className="card">
        <div className="card-head"><h2>Player stats</h2></div>
        <p className="muted">
          {opens
            ? `Nothing to rank until the first results, on ${opens}. From that night:`
            : 'Nothing to rank until the first results are in. From that night:'}
        </p>
        <ul className="coming">
          <li>
            <b>Every player in the league, ranked</b> — sortable by points, matches won, sets,
            games and tie-breaks, and filterable to one {(league?.unitNounSingular ?? 'team').toLowerCase()}
          </li>
          <li>
            <b>{TRUERANK}</b> — the rating you were seeded at, then where your results take it,
            moving after every match
          </li>
          <li>
            <b>Giant killers</b> — pairs who beat opponents rated above them, biggest upset first
          </li>
          <li><b>Most upsets</b> — who does it most often across the season</li>
          <li><b>Movers</b> — the biggest rating climbs and falls of each match day</li>
        </ul>
        {example && (
          <p className="muted foot-note">
            {example.name} has all of this already, from {example.stats?.matchesPlayed} matches —
            pick it from the Season menu above to see it.
          </p>
        )}
      </section>
    );
  }

  return (
    <>
      <section className="card">
        <div className="card-head"><h2>Player stats</h2></div>

        <div className="stats-controls">
          <label>
            <span className="sr-only">Team</span>
            <select value={team} onChange={(e) => setTeam(e.target.value)}>
              <option value="">All {(league?.unitNoun ?? 'teams').toLowerCase()}</option>
              {[...new Set(rows.map((r) => r.team))].sort().map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </label>
          <label>
            <span className="sr-only">Sort by</span>
            <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
              {SORTS.filter((s) => {
                if (s.key === 'rating') return !tiered;
                if (s.key === 'dd') return anyDd;
                if (s.key === 'tiebreaks') return anyTb;
                return true;
              }).map((s) => (
                <option key={s.key} value={s.key}>
                  {/*
                    Named for what the column actually holds. An archived season
                    has seeds and no TrueRank, and offering "Sort: TrueRank™"
                    over a column headed "Rating" claims the season measured
                    something it never did.
                  */}
                  Sort: {s.key === 'rating' && !rated ? 'Rating' : s.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th className="num">#</th>
                <th>Player</th>
                {/* Beside the name, not at the far end. This board is SORTED
                    by total points and the column was the last of eleven, off
                    the right edge of a phone — the one figure it is ordered by
                    was the one you had to scroll for, and scrolling to it put
                    the name out of view. Same move as the squad tables. */}
                <th className="num strong">Pts</th>
                {!tiered && <th className="num">{rated ? TRUERANK : 'Rating'}</th>}
                <th className="num">P</th>
                <NumHead full="Won" abbr="W" title="Matches won" />
                <NumHead full="Sets" abbr="S" title="Sets won" />
                <NumHead full="Games" abbr="G" title="Games won" />
                {anyDd && <th className="num">DD</th>}
                {anyTb && <th className="num">TB</th>}
              </tr>
            </thead>
            <tbody>
              {shown.map((row, i) => (
                <tr key={`${row.teamId ?? 'sub'}-${row.player.id}`}>
                  <td className="num muted-cell">{i + 1}</td>
                  <NameAndTeam name={row.player.name} team={row.team} />
                  <td className="num strong">{n(row.player.stats?.totalPoints)}</td>
                  {!tiered && (
                    <td className={row.player.trueRank ? 'num tr-value' : 'num'}>
                      {row.player.trueRank
                        ? ratingText(row.player.trueRank.rating)
                        : ratingText(row.player.rating)}
                    </td>
                  )}
                  <td className="num">{n(row.player.stats?.played)}</td>
                  <td className="num">{n(row.player.stats?.matchesWon)}</td>
                  <td className="num">{n(row.player.stats?.setsWon)}</td>
                  <td className="num">{n(row.player.stats?.gamesWon)}</td>
                  {anyDd && <td className="num">{n(row.player.stats?.ddPoints)}</td>}
                  {anyTb && (
                    <td className="num">
                      {row.player.tieBreaks?.played
                        ? `${row.player.tieBreaks.won}/${row.player.tieBreaks.played}`
                        : '–'}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* The attribution belongs where the mark is used, and an archived
            season that predates the engine does not use it. */}
        <p className="muted foot-note">
          {shown.length} players{rated ? `. ${TRUERANK_BY}.` : '.'}
        </p>
      </section>

      {winRates && winRates.players.length > 0 && (
        <section className="card">
          <div className="card-head">
            <h2>Win rate</h2>
          </div>
          <p className="muted">
            Every pairing a player has played, and how many of them their pair won. Level on
            win rate, the player with more games ranks higher.
          </p>

          {/*
            The top three called out, then everyone. The board exists so a
            player can see whether they made it, and a name in a list of forty
            does not answer that — but a top three with no list under it does
            not let them see how close they came either.
          */}
          <ol className="wr-podium">
            {winRates.players
              .filter((r) => r.rank <= 3)
              .map((r) => (
              <li className={r.rank === 1 ? 'wr-place is-first' : 'wr-place'} key={r.playerId}>
                <span className="wr-rank">{r.rank}</span>
                <span className="wr-who">
                  <b>{r.name}</b>
                  <small className="muted">{r.team}</small>
                </span>
                <span className="wr-rate">{percent(r.rate)}</span>
                <span className="wr-record muted">{record(r)}</span>
              </li>
            ))}
          </ol>

          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Player</th>
                  <th className="num strong">Win rate</th>
                  <th className="num">P</th>
                  <th className="num">W</th>
                  <th className="num">D</th>
                  <th className="num">L</th>
                </tr>
              </thead>
              <tbody>
                {winRates.players.map((r) => (
                  <tr key={r.playerId}>
                    <td>{r.rank}</td>
                    <NameAndTeam name={r.name} team={r.team} />
                    <td className="num strong">{percent(r.rate)}</td>
                    <td className="num">{r.played}</td>
                    <td className="num">{r.won}</td>
                    <td className="num">{r.drawn}</td>
                    <td className="num">{r.lost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/*
            Said outright, because a percentage nobody can reproduce from the
            W-D-L beside it reads as a mistake. Only shown when there is a draw
            to explain: Season 2 cannot draw a pairing, so on the live board
            this line would be answering a question nobody asked.
          */}
          {winRates.players.some((r) => r.drawn > 0) && (
            <p className="muted">
              A shared pairing counts as half a win, the same as the points it splits.
            </p>
          )}
        </section>
      )}

      {kills.length > 0 && (
        <section className="card">
          <div className="card-head"><h2>Giant killers</h2></div>
          <p className="muted">
            Pairs who beat opponents rated above them — the biggest upsets of the season.
          </p>
          <div className="gk-list">
            {kills.map((kill, i) => (
              <div className="gk" key={`${kill.gameweek}-${i}`}>
                <p className="gk-week">{roundLabel(kill.stage ?? 'league', kill.gameweek)}</p>
                <div className="gk-side">
                  <p className="gk-winners">{kill.winners.map((p) => p.name).join(' & ')}</p>
                  <span className={`gk-rating${tv}`}>{ratingText(kill.wonAt)}</span>
                </div>
                <div className="gk-side">
                  <p className="gk-beaten">beat {kill.beaten.map((p) => p.name).join(' & ')}</p>
                  <span className={`gk-rating${tv}`}>{ratingText(kill.beatenAt)}</span>
                </div>
                <p className="gk-gap">+{ratingText(kill.gap)} rating gap overcome</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {upsets.length > 0 && (
        <section className="card">
          <div className="card-head"><h2>Most upsets</h2></div>
          <p className="muted">Wins over a higher-rated pair, counted for both players.</p>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th className="num">#</th>
                  <th>Player</th>
                  <th className="num">Upsets</th>
                </tr>
              </thead>
              <tbody>
                {upsets.map((row, i) => (
                  <tr key={row.playerId}>
                    <td className="num muted-cell">{i + 1}</td>
                    <NameAndTeam name={row.name} team={row.team} />
                    <td className="num gk-count">{row.upsets}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {movers && (movers.risers.length > 0 || movers.fallers.length > 0) && (
        <section className="card">
          <div className="card-head"><h2>Movers</h2></div>
          <p className="muted">
            {/* "on Match Day 3" but "in the Semi-finals" — the preposition
                follows the kind of round, so one template cannot serve both. */}
            Biggest rating changes{' '}
            {(movers.stage ?? 'league') === 'league'
              ? `on ${roundLabel('league', movers.gameweek)}`
              : `in the ${roundLabel(movers.stage!, movers.gameweek)}`}.
          </p>
          <div className="movers">
            <Movers title="Climbing" rows={movers.risers} />
            <Movers title="Slipping" rows={movers.fallers} />
          </div>
        </section>
      )}
    </>
  );
}

function Movers({ title, rows }: { title: string; rows: RatingMove[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="mover-col">
      <p className="mover-head">{title}</p>
      {rows.map((row) => (
        <div className="mover" key={row.playerId}>
          <div>
            <p className="mover-name">{row.name}</p>
            {/* The games behind the swing, because a 0.09 off one game and a
                0.09 off four are different claims about a player. */}
            <p className="mover-team">
              {row.team} · {row.matches} {row.matches === 1 ? 'game' : 'games'}
            </p>
          </div>
          <div className="mover-nums">
            <span className={row.delta > 0 ? 'mover-delta up' : 'mover-delta down'}>
              {row.delta > 0 ? '+' : '−'}
              {ratingText(Math.abs(row.delta))}
            </span>
            <span className="mover-range tr-value">
              {ratingText(row.before)} <span aria-hidden="true">→</span> {ratingText(row.after)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * A rate as a percentage, to whole numbers.
 *
 * No decimal place, deliberately. 87.5% and 88% say the same thing to a player
 * checking whether they made the top three, and the W-D-L beside it is the
 * exact record for anyone who wants to do the arithmetic themselves.
 */
const percent = (rate: number) => `${Math.round(rate * 100)}%`;

/** "6W 2D 0L", the record behind the percentage. */
const record = (r: WinRate) => `${r.won}W ${r.drawn}D ${r.lost}L`;
